import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, MapPin, CheckCircle, XCircle, Flag, Globe, ArrowClockwise, FloppyDisk } from "@phosphor-icons/react";

const STATUS_META = {
  ACTIVE: { label: "IT · France (visibles)", color: "#22C55E" },
  REVIEW_REQUIRED: { label: "À valider (masqués)", color: "#F59E0B" },
  FILTERED_NON_IT: { label: "Non-IT (filtrés)", color: "#FB7185" },
  FILTERED_NON_FRANCE: { label: "Hors France (filtrés)", color: "#94A3B8" },
};
const REVIEW_TABS = [
  { key: "REVIEW_REQUIRED", label: "À valider" },
  { key: "FILTERED_NON_IT", label: "Non-IT" },
  { key: "FILTERED_NON_FRANCE", label: "Hors France" },
];

function Stat({ label, value, color }) {
  return (
    <div className="surface rounded-lg p-4" data-testid={`it-stat-${label}`}>
      <div className="overline !text-muted mb-1">{label}</div>
      <div className="font-display text-2xl font-bold" style={{ color: color || "#fff" }}>{value ?? "—"}</div>
    </div>
  );
}

export default function AdminITMarket() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(80);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("REVIEW_REQUIRED");
  const [review, setReview] = useState({ items: [], total: 0, counts: {} });
  const [reviewLoading, setReviewLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/signals/it-stats");
      setStats(data);
      setMinScore(data.min_it_relevance_score);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  const loadReview = useCallback(async (status) => {
    setReviewLoading(true);
    try {
      const { data } = await api.get(`/admin/signals/review?status=${status}&limit=50`);
      setReview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReviewLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadReview(tab); }, [tab, loadReview]);

  const saveConfig = async () => {
    try {
      await api.put("/admin/signals/it-config", { min_it_relevance_score: Number(minScore) });
      toast.success(`Seuil IT enregistré : ${minScore}`);
      loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runReclassify = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signals/reclassify", { dry_run: dry, use_llm: !dry, llm_cap: 200 });
      const s = data.stats;
      toast.success(`${dry ? "Simulation" : "Appliqué"} — ACTIVE ${s.it_active} · REVIEW ${s.review_required} · non-IT ${s.non_it} · hors-FR ${s.non_france}`, { duration: 7000 });
      loadStats(); loadReview(tab);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/signals/${id}/it-action`, { action });
      toast.success(action === "CONFIRM_IT" ? "Confirmé IT" : action === "CONFIRM_NON_IT" ? "Confirmé non-IT" : "Reclassé");
      setReview((r) => ({ ...r, items: r.items.filter((it) => it.id !== id), total: Math.max(0, r.total - 1) }));
      loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const vis = stats?.visibility || {};

  return (
    <div className="space-y-6" data-testid="admin-it-market">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <ShieldCheck size={24} className="text-accent" /> IT-only &amp; France-only
          </h1>
          <p className="text-muted text-sm mt-1">Règles non négociables : interKO n'indexe que des signaux IT localisés en France.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => runReclassify(true)} disabled={busy} data-testid="it-dryrun-btn"
            className="btn-ghost text-sm flex items-center gap-1.5"><ArrowClockwise size={15} /> Simuler</button>
          <button onClick={() => runReclassify(false)} disabled={busy} data-testid="it-apply-btn"
            className="btn-primary text-sm flex items-center gap-1.5"><ArrowClockwise size={15} /> Reclasser &amp; appliquer</button>
        </div>
      </div>

      {/* Marché actif */}
      <div className="surface rounded-lg p-4" data-testid="it-market-banner">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-accent" />
          <span className="overline !text-muted">Marché actif</span>
          <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-mono bg-accent/15 text-accent">FRANCE</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {stats && Object.entries(stats.market_config || {}).map(([cc, m]) => (
            <span key={cc} data-testid={`market-${cc}`}
              className={`px-2.5 py-1 rounded-md text-xs font-mono flex items-center gap-1.5 ${m.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-surface-2 text-muted"}`}>
              {m.enabled ? <CheckCircle size={13} /> : <XCircle size={13} />} {cc} · {m.name}
            </span>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-muted">
          <Globe size={13} /> Tous les providers (HasData/Indeed, JSearch, SerpApi, Adzuna, France Travail…) héritent de <span className="font-mono text-fg">country = FR</span>. La collecte USA est désactivée.
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total" value={stats?.total} />
            <Stat label="IT · France" value={vis.ACTIVE} color={STATUS_META.ACTIVE.color} />
            <Stat label="À valider" value={vis.REVIEW_REQUIRED || 0} color={STATUS_META.REVIEW_REQUIRED.color} />
            <Stat label="Non-IT" value={vis.FILTERED_NON_IT || 0} color={STATUS_META.FILTERED_NON_IT.color} />
            <Stat label="Hors France" value={vis.FILTERED_NON_FRANCE || 0} color={STATUS_META.FILTERED_NON_FRANCE.color} />
          </div>

          {/* Config seuil */}
          <div className="surface rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid="it-config">
            <span className="overline !text-muted">Seuil d'affichage IT (0-100)</span>
            <input type="number" min={0} max={100} value={minScore} onChange={(e) => setMinScore(e.target.value)}
              className="w-24 bg-surface-2 border border-border rounded-md px-3 py-1.5 text-sm" data-testid="it-min-score-input" />
            <button onClick={saveConfig} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="it-save-config">
              <FloppyDisk size={15} /> Enregistrer</button>
            <span className="text-xs text-muted">Un signal sous ce seuil n'apparaît pas dans la recherche, les alertes ni le matching.</span>
          </div>

          {/* Providers */}
          <div className="surface rounded-lg p-4" data-testid="it-provider-breakdown">
            <div className="overline !text-muted mb-3">Qualité par provider</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-muted text-xs border-b border-border">
                  <th className="text-left py-2">Provider</th><th className="text-right">Reçus</th>
                  <th className="text-right">IT·FR</th><th className="text-right">Hors FR</th>
                  <th className="text-right">Non-IT</th><th className="text-right">À valider</th>
                </tr></thead>
                <tbody>
                  {stats && Object.entries(stats.provider || {}).map(([p, v]) => (
                    <tr key={p} className="border-b border-border/50">
                      <td className="py-1.5">{p}</td>
                      <td className="text-right font-mono">{v.received}</td>
                      <td className="text-right font-mono text-emerald-400">{v.ACTIVE || 0}</td>
                      <td className="text-right font-mono text-muted">{v.FILTERED_NON_FRANCE || 0}</td>
                      <td className="text-right font-mono text-rose-400">{v.FILTERED_NON_IT || 0}</td>
                      <td className="text-right font-mono text-amber-400">{v.REVIEW_REQUIRED || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Revue */}
          <div className="surface rounded-lg p-4" data-testid="it-review">
            <div className="flex items-center gap-2 mb-3">
              <Flag size={16} className="text-accent" />
              <span className="overline !text-muted">Revue des signaux</span>
            </div>
            <div className="flex gap-2 mb-4">
              {REVIEW_TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`it-tab-${t.key}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-mono ${tab === t.key ? "bg-accent/15 text-accent" : "bg-surface-2 text-muted hover:text-fg"}`}>
                  {t.label} · {review.counts?.[t.key] ?? 0}
                </button>
              ))}
            </div>
            {reviewLoading ? (
              <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
            ) : review.items.length === 0 ? (
              <div className="text-muted text-sm py-6 text-center">Aucun signal dans cette catégorie.</div>
            ) : (
              <div className="space-y-2">
                {review.items.map((it) => (
                  <div key={it.id} className="bg-surface-2 rounded-md p-3 flex items-start justify-between gap-4" data-testid={`it-review-row-${it.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{it.role_title || "—"}</div>
                      <div className="text-xs text-muted truncate">{it.company || "—"} · {it.city || it.location_raw || "—"} · {it.source}</div>
                      <div className="text-xs text-muted mt-1 line-clamp-2">{it.excerpt}</div>
                      <div className="flex items-center gap-2 mt-1.5 text-xs font-mono">
                        <span className="px-1.5 py-0.5 rounded bg-bg text-accent">IT {it.it_relevance_score}</span>
                        {it.country_normalized && <span className="px-1.5 py-0.5 rounded bg-bg text-muted">{it.country_normalized}</span>}
                        {it.canonical_family_name && <span className="px-1.5 py-0.5 rounded bg-bg text-muted">{it.canonical_family_name}</span>}
                        <span className="text-muted">{it.it_reason}</span>
                      </div>
                    </div>
                    {tab !== "FILTERED_NON_FRANCE" && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <button onClick={() => act(it.id, "CONFIRM_IT")} data-testid={`it-confirm-it-${it.id}`}
                          className="text-xs px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">Confirmer IT</button>
                        <button onClick={() => act(it.id, "CONFIRM_NON_IT")} data-testid={`it-confirm-nonit-${it.id}`}
                          className="text-xs px-2.5 py-1 rounded-md bg-rose-500/15 text-rose-400 hover:bg-rose-500/25">Non-IT</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
